import { LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { getFromStorage, removeFromStorage } from '@helpers/storage.helpers'
import { useStore } from '@hooks/useStore'
import { Error } from '@mui/icons-material'
import {
  Alert,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormHelperText,
  Tooltip,
} from '@mui/material'
import { IOVErrorResponseData, IUserLoginCreds } from '@typings'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { loginValidationSchema } from './validation/login.form.validation'
import { OvCard } from '@components'

export const LoginForm = observer(() => {
  const { auth } = useStore()
  const [formError, setFormError] = useState<string | null>(null)
  const navigate = useNavigate()

  const form = useForm<IUserLoginCreds>({
    resolver: yupResolver(loginValidationSchema()),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'all',
  })

  const { formState } = form
  const intended = getFromStorage('intended')

  const handleLogin: SubmitHandler<IUserLoginCreds> = async data => {
    setFormError(null)
    await auth
      .login(data)
      .then(() => {
        if (intended) {
          removeFromStorage('intended')
          navigate(intended)
        } else {
          navigate(URL_PATHS.DASHBOARD)
        }
      })
      .catch((errors: IOVErrorResponseData) => handleFormErrors(errors))
  }

  const handleFormErrors = (data: IOVErrorResponseData) => {
    const { message } = data
    if (message) setFormError(message)
  }

  const sumitForm = () => {
    form.handleSubmit(handleLogin)()
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={sumitForm}
      loading={auth.loading}
      enableNavigationBlocker={false}>
      <OvForm>
        <OvCard
          elevation={4}
          sx={{
            width: '100%',
            minWidth: { xs: 'auto', md: 450 },
            maxWidth: 500,
          }}>
          <CardHeader
            title={LABELS.LOGIN}
            sx={{ pb: 1 }}
          />
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 2,
            }}>
            <OvFormInput
              name="email"
              inputProps={{ type: 'email' }}
              label={LABELS.EMAIL}
              required
            />
            <OvFormInput
              name="password"
              type="password"
              label={LABELS.PASSWORD}
              required
            />
            <Collapse in={!!formError}>
              <Alert
                color="error"
                icon={<Error />}>
                <FormHelperText error>{formError}</FormHelperText>
              </Alert>
            </Collapse>
          </CardContent>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              flexDirection: 'column',
              gap: 1,
            }}>
            <Tooltip title={TOOLTIPS.LOGIN_BUTTON}>
              <Button
                disabled={!formState.isValid || formState.isSubmitting || auth.loading}
                variant="contained"
                type="submit"
                fullWidth>
                {LABELS.LOGIN}
              </Button>
            </Tooltip>
            <Tooltip title={TOOLTIPS.FORGOT_PASSWORD_LINK}>
              <Button
                variant="text"
                color="secondary"
                disabled={formState.isSubmitting}
                size="small"
                onClick={() => navigate(URL_PATHS.FORGOT_PASSWORD)}>
                {LABELS.PASSWORD_FORGOT}
              </Button>
            </Tooltip>
          </CardActions>
          <Divider sx={{ '& .MuiDivider-wrapper': { fontFamily: 'Roboto Slab' }, my: 2 }}>
            {LABELS.NO_ACCOUNT}
          </Divider>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              justifyContent: 'center',
            }}>
            <Tooltip title={TOOLTIPS.GO_TO_REGISTER}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate(URL_PATHS.SIGNUP)}>
                {LABELS.REGISTER}
              </Button>
            </Tooltip>
          </CardActions>
        </OvCard>
      </OvForm>
    </OvFormProvider>
  )
})
